<template>
    <DCard padding>
        <DRow>
            <DCol>
                <DTable
                    :key="tableKey"
                    :items="data"
                    :fields="fields"
                    hover
                >
                    <template #status="{item}">
                        <td>
                            <DTag
                                :label="__('contracts.pricings.statuses.' + item.status)"
                                :color="getStatusTagColor(item.status)"
                            />
                        </td>
                    </template>
                    <template #mailStatus="{item}">
                        <td v-if="item.mail_status === 'not_informed'">
                            N/A
                        </td>
                        <td v-else>
                            <DTag
                                :label="__('contracts.pricings.statuses.' + item.mail_status)"
                                :color="getStatusTagColor(item.mail_status)"
                            />
                        </td>
                    </template>
                    <template #actions="{item}">
                        <td>
                            <div style="display: flex; grid-gap: 10px">
                                <DButton
                                    icon="eye"
                                    mini
                                    @click="preview(item.id)"
                                />
                                <DDropdown
                                    :id="`pricing_actions_dropdown_${item.id}`"
                                    :class="'more-actions-button'"
                                    :caret="false"
                                    icon="dots-horizontal"
                                >
                                    <DDropdownItem
                                        :id="`pricing_actions_download_file_${item.id}`"
                                        @click="downloadFile(item.id)"
                                    >
                                        {{ __('contracts.pricings.download_file') }}
                                    </DDropdownItem>
                                    <DDropdownItem
                                        v-if="item.status !== 'draft' && item.status !== 'open' && item.mail_status !== null"
                                        :id="`pricing_actions_email_status_${item.id}`"
                                        @click="openModal(item.id)"
                                    >
                                        {{ __('contracts.pricings.email_transmission') }}
                                    </DDropdownItem>
                                </DDropdown>
                            </div>
                        </td>
                    </template>
                </DTable>
                <DPagination
                    v-if="files.last_page >= 1"
                    :active-page="files.current_page"
                    :pages="files.last_page"
                    align="center"
                    @update:activePage="changePage"
                />
            </DCol>
        </DRow>
        <DFabs>
            <DFab
                id="btn-preview-new-pricing"
                icon="plus"
                :label="__('contracts.pricings.upload')"
                @click="upload"
            />
        </DFabs>
        <EmailStatsModal
            v-if="showModal === true"
            :show-modal="showModal"
            :pricing-upload="pricingUploadId"
            @close="closeModal"
        />
    </DCard>
</template>
<script setup>
import { router, usePage } from '@inertiajs/vue3'
import { route } from '~@/Composables/route'
import { __ } from '~@/Composables/translation'
import { ref, onMounted } from 'vue'
import EmailStatsModal from '~@/Components/Contracts/Pricing/EmailStatsModal'

const props = defineProps({
    items: {
        type: Array,
        default: () => {
            return []
        }
    },
    files: {
        type: Object,
        default: () => {
            return {}
        }
    }
})

const tableKey = ref(0)
const data = ref([])

onMounted(() => {
    window.laravelEcho.channel('onecrm.pricing.upload').listen('.pricing.upload_updated', socketEvent => {
        handleStatusUpdate(socketEvent)
    })
    data.value = props.items
})

const fields = [
    { key: 'id', label: __('contracts.pricings.headers.id') },
    { key: 'name', label: __('contracts.pricings.headers.name') },
    { key: 'date', label: __('contracts.pricings.headers.date') },
    { key: 'contracts', label: __('contracts.pricings.headers.contracts') },
    { key: 'employee', label: __('contracts.pricings.headers.employee') },
    { key: 'status', label: __('contracts.pricings.headers.status') },
    { key: 'mailStatus', label: __('contracts.pricings.headers.mail_status') },
    { key: 'actions', label: '', _style: { minWidth: '5%' } }
]
const downloadFile = (id) => {
    const link = document.createElement('a')
    link.href = route('contracts.pricing.download', id)
    document.body.appendChild(link)
    link.click()
    document.body.removeChild(link)
}

const preview = (id) => router.get(route('contracts.pricing.preview', id))
const upload = () => router.get(route('contracts.pricing.upload-page'))

const changePage = (page) => {
    const query = {}
    if (page > 1) {
        query.page = page
    }

    router.get(route('contracts.pricing.index'), query)
}

const getStatusTagColor = (status) => {
    if (status === 'draft' || status === 'in_progress') {
        return 'primary'
    }
    if (status === 'open' || status === 'not_sent' || status === 'in_progress') {
        return 'secondary'
    }
    if (status === 'success' || status === 'sent') {
        return 'success'
    }
    return 'danger'
}

const showModal = ref(false)

const pricingUploadId = ref(null)

const openModal = (fileId) => {
    pricingUploadId.value = fileId
    showModal.value = true
}

const closeModal = () => {
    showModal.value = false
}

const handleStatusUpdate = (socketEvent) => {
    // do something only if event came from same tenant
    if (socketEvent.tenant !== usePage().props.tenant) {
        return
    }
    const pricingUpload = socketEvent.pricingUpload
    const existingRequestId = data.value.findIndex(existingRequest => existingRequest.id === pricingUpload.id)

    // remove highlight-row class from selected row after 3000ms
    setTimeout(() => {
        pricingUpload._classes = ''
        data.value.splice(existingRequestId, 1, pricingUpload)
        tableKey.value++
    }, 3000)

    pricingUpload._classes = pricingUpload.status === 'failed' ? 'highlight-row' : 'highlight-row-success'
    if (
        pricingUpload.mail_status !== null ||
        pricingUpload.mail_status !== 'in_progress'
    ) {
        pricingUpload._classes = pricingUpload.mail_status === 'failed' ? 'highlight-row' : 'highlight-row-success'
    }
    data.value.splice(existingRequestId, 1, pricingUpload)
}

</script>
<style scoped lang="scss">

</style>
